// Plate's URL input runs `encodeURI` on every change before `transformInput`
// sees the value, so stray whitespace from a paste arrives as `%20`/`%0A`
// rather than as characters `String.prototype.trim` can remove.
const EDGE_WHITESPACE =
  /^(?:\s|%20|%09|%0A|%0D|%C2%A0)+|(?:\s|%20|%09|%0A|%0D|%C2%A0)+$/gi;

/**
 * Normalize the raw text typed into the link floating toolbar's URL field
 * before Plate validates it.
 *
 * Plate's `LinkPlugin` validates input with the `is-url` package, which only
 * accepts URLs that already include a scheme — `https://google.com` passes but
 * a bare `google.com` does not. Without this transform, `submitFloatingLink`
 * fails that validation and silently returns, so clicking "Apply" with a normal
 * pasted/typed domain appears to do nothing.
 *
 * Wired in via the `transformInput` plugin option (see `editor-plugins.ts`),
 * which `submitFloatingLink` applies to the URL before validation. Returning
 * `undefined` for empty input leaves the no-op behavior intact for a blank
 * field.
 */
export function transformLinkInput(url: string): string | undefined {
  const trimmed = url.replace(EDGE_WHITESPACE, "");
  if (!trimmed) {
    return undefined;
  }
  // Relative links and in-page anchors are accepted by Plate as-is.
  if (trimmed.startsWith("/") || trimmed.startsWith("#")) {
    return trimmed;
  }
  // Already has an explicit scheme (https:, mailto:, tel:, …) or is
  // protocol-relative (//host) — leave the user's intent untouched.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) || trimmed.startsWith("//")) {
    return trimmed;
  }
  // Bare domain like `google.com` or `www.google.com` — default to https.
  return `https://${trimmed}`;
}
